/**
 * Version History Modal - Matching Figma Design
 * Modal view for template version history with compare and restore capabilities
 */

import { useState, useEffect } from 'react';
import { 
  X,
  Plus,
  GitCompare,
  CheckCircle,
  Clock,
  Archive,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  useTemplateVersions,
  useCreateTemplateVersion,
  useActivateVersion,
} from '@/hooks/use-templates';
import { 
  TemplateStatus, 
  TEMPLATE_STATUS_LABELS,
} from '@audra-flow/types';
import { useToast } from '@/hooks/use-toast';

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateName: string;
}

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const configs: Record<string, { bg: string; text: string; border: string; icon: React.ReactNode }> = {
    ACTIVE: { 
      bg: 'bg-green-50', 
      text: 'text-green-700', 
      border: 'border-green-200',
      icon: <CheckCircle className="w-3.5 h-3.5" /> 
    },
    DRAFT: { 
      bg: 'bg-gray-50', 
      text: 'text-gray-600', 
      border: 'border-gray-200',
      icon: <Clock className="w-3.5 h-3.5" /> 
    },
    IN_REVIEW: { 
      bg: 'bg-blue-50', 
      text: 'text-blue-700', 
      border: 'border-blue-200',
      icon: <Clock className="w-3.5 h-3.5" /> 
    },
    DEPRECATED: { 
      bg: 'bg-orange-50', 
      text: 'text-orange-600', 
      border: 'border-orange-200',
      icon: <Archive className="w-3.5 h-3.5" /> 
    },
  };
  const config = configs[status] || configs.DRAFT;
  const label = TEMPLATE_STATUS_LABELS[status as TemplateStatus] || status;
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded border',
      config.bg, config.text, config.border
    )}>
      {config.icon}
      {label}
    </span>
  );
}

export function VersionHistoryModal({ 
  isOpen, 
  onClose, 
  templateId, 
  templateName 
}: VersionHistoryModalProps) {
  const { toast } = useToast();
  const { data: versions, isLoading, refetch } = useTemplateVersions(templateId);
  const createVersion = useCreateTemplateVersion(templateId);
  const activateVersion = useActivateVersion(templateId);
  
  // Create version modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newVersion, setNewVersion] = useState('');
  const [changelog, setChangelog] = useState('');
  
  // Compare versions state
  const [showCompare, setShowCompare] = useState(false);
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [comparisonData, setComparisonData] = useState<{
    versionA: { version: string; content: Record<string, unknown> | null; changelog: string | null };
    versionB: { version: string; content: Record<string, unknown> | null; changelog: string | null };
  } | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowCreateModal(false);
      setShowCompare(false);
      setVersionA('');
      setVersionB('');
    }
  }, [isOpen]);

  // Suggest next version
  const suggestNextVersion = () => {
    if (!versions || versions.length === 0) return '1.0.0';
    const latest = versions[0].version;
    const parts = latest.split('.').map(Number);
    if (parts.length === 3) {
      parts[2] += 1; // Increment patch
      return parts.join('.');
    }
    return '1.0.0';
  };

  const handleCreateVersion = async () => {
    if (!newVersion.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Version number is required.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createVersion.mutateAsync({
        version: newVersion,
        content: {},
        changelog: changelog || undefined,
      });
      toast({
        title: 'Version created',
        description: `Version ${newVersion} has been created.`,
      });
      setShowCreateModal(false);
      setNewVersion('');
      setChangelog('');
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create version.',
        variant: 'destructive',
      });
    }
  };

  const handleSetAsActive = async (versionId: string, versionNumber: string) => {
    try {
      await activateVersion.mutateAsync(versionId);
      toast({
        title: 'Version activated',
        description: `Version ${versionNumber} is now active. All other versions are deprecated.`,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to activate version.',
        variant: 'destructive',
      });
    }
  };

  const handleCompare = () => {
    if (!versionA || !versionB) {
      toast({
        title: 'Select versions',
        description: 'Please select two versions to compare.',
        variant: 'destructive',
      });
      return;
    }
    
    if (versionA === versionB) {
      toast({
        title: 'Same version selected',
        description: 'Please select two different versions to compare.',
        variant: 'destructive',
      });
      return;
    }
    
    // Find the version data
    const verA = versions?.find(v => v.version === versionA);
    const verB = versions?.find(v => v.version === versionB);
    
    if (verA && verB) {
      setComparisonData({
        versionA: { version: verA.version, content: verA.content, changelog: verA.changelog },
        versionB: { version: verB.version, content: verB.content, changelog: verB.changelog },
      });
      setShowCompareModal(true);
    }
  };
  
  // Compare version strings (returns positive if a > b, negative if a < b, 0 if equal)
  const compareVersions = (a: string, b: string): number => {
    const partsA = a.split('.').map(Number);
    const partsB = b.split('.').map(Number);
    const maxLength = Math.max(partsA.length, partsB.length);
    
    for (let i = 0; i < maxLength; i++) {
      const numA = partsA[i] || 0;
      const numB = partsB[i] || 0;
      if (numA > numB) return 1;
      if (numA < numB) return -1;
    }
    return 0;
  };

  // Human-readable field labels
  const fieldLabels: Record<string, string> = {
    name: 'Template Name',
    description: 'Description',
    type: 'Template Type',
    category: 'Category',
    icon: 'Icon',
    instructions: 'AI Instructions',
    conversationStarters: 'Conversation Starters',
    outputFormatSpec: 'Output Format',
    schema: 'Schema',
  };

  // Format values for display
  const formatValue = (key: string, value: unknown): string => {
    if (value === null || value === undefined) return '';
    
    // Type labels
    const typeLabels: Record<string, string> = {
      PERSONA: 'Persona',
      USE_CASE: 'Use Case',
      STORY: 'User Story',
      REQUIREMENT: 'Requirement',
      TECHNICAL_SPEC: 'Technical Specification',
      DESIGN_SPEC: 'Design Specification',
      TEST_CASE: 'Test Case',
      RELEASE_NOTE: 'Release Note',
    };
    
    // Category labels
    const categoryLabels: Record<string, string> = {
      discovery: 'Discovery',
      definition: 'Definition',
      design: 'Design',
      delivery: 'Delivery',
    };
    
    if (key === 'type' && typeof value === 'string') {
      return typeLabels[value] || value;
    }
    
    if (key === 'category' && typeof value === 'string') {
      return categoryLabels[value] || value;
    }
    
    if (Array.isArray(value)) {
      return value.join(', ') || '(empty)';
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    
    return String(value);
  };

  // Get differences between two objects
  const getDifferences = (objA: Record<string, unknown> | null, objB: Record<string, unknown> | null) => {
    const differences: { key: string; label: string; valueA: unknown; valueB: unknown; type: 'added' | 'removed' | 'changed' }[] = [];
    
    const a = objA || {};
    const b = objB || {};
    const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
    
    allKeys.forEach(key => {
      const valA = a[key];
      const valB = b[key];
      const strA = JSON.stringify(valA);
      const strB = JSON.stringify(valB);
      
      if (strA !== strB) {
        const label = fieldLabels[key] || key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
        
        if (valA === undefined) {
          differences.push({ key, label, valueA: null, valueB: valB, type: 'added' });
        } else if (valB === undefined) {
          differences.push({ key, label, valueA: valA, valueB: null, type: 'removed' });
        } else {
          differences.push({ key, label, valueA: valA, valueB: valB, type: 'changed' });
        }
      }
    });
    
    return differences;
  };

  // Parse changelog into bullet points
  const parseChangelog = (changelog: string | null) => {
    if (!changelog) return [];
    // Split by newlines or bullet points
    return changelog
      .split(/\n|•|·|-/)
      .map(item => item.trim())
      .filter(item => item.length > 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Version History</h2>
            <p className="text-sm text-gray-500 mt-0.5">{templateName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => {
                setNewVersion(suggestNextVersion());
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm"
            >
              <Plus className="w-4 h-4" />
              Create New Version
            </button>
            <button
              onClick={() => setShowCompare(!showCompare)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors font-medium text-sm",
                showCompare 
                  ? "bg-indigo-50 border-indigo-200 text-indigo-700" 
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              )}
            >
              <GitCompare className="w-4 h-4" />
              Compare Versions
            </button>
          </div>
          
          {/* Compare toolbar */}
          {showCompare && (
            <div className="flex items-center gap-3">
              <select
                value={versionA}
                onChange={(e) => setVersionA(e.target.value)}
                className="min-w-[160px] px-4 py-2.5 border border-gray-300 rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 appearance-none cursor-pointer"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px', paddingRight: '40px' }}
              >
                <option value="">Select version A</option>
                {versions?.map((v) => (
                  <option key={v.id} value={v.version}>v{v.version}</option>
                ))}
              </select>
              <span className="text-gray-500 text-sm font-medium">vs</span>
              <select
                value={versionB}
                onChange={(e) => setVersionB(e.target.value)}
                className="min-w-[160px] px-4 py-2.5 border border-gray-300 rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 appearance-none cursor-pointer"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px', paddingRight: '40px' }}
              >
                <option value="">Select version B</option>
                {versions?.map((v) => (
                  <option key={v.id} value={v.version}>v{v.version}</option>
                ))}
              </select>
              <button
                onClick={handleCompare}
                disabled={!versionA || !versionB}
                className="px-6 py-2.5 bg-gray-700 text-white rounded-full hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                Compare
              </button>
            </div>
          )}
        </div>

        {/* Versions List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : !versions || versions.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No versions yet</p>
            </div>
          ) : (
            versions.map((version, index) => {
              const isLatest = index === 0;
              const isActive = version.status === 'ACTIVE';
              const changes = parseChangelog(version.changelog);
              
              return (
                <div 
                  key={version.id}
                  className={cn(
                    "border rounded-xl p-5 transition-colors",
                    isLatest && isActive
                      ? "border-indigo-200 bg-indigo-50/50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  )}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-900">
                        v{version.version}
                      </span>
                      <StatusBadge status={version.status} />
                      {isLatest && (
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                          Latest
                        </span>
                      )}
                    </div>
                    {!isActive && (
                      <button
                        onClick={() => handleSetAsActive(version.id, version.version)}
                        disabled={activateVersion.isPending}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {activateVersion.isPending ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Activating...
                          </>
                        ) : (
                          'Set as Active'
                        )}
                      </button>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-3">
                    {new Date(version.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                    {version.createdBy?.name && (
                      <span className="ml-2">• by {version.createdBy.name}</span>
                    )}
                  </p>
                  
                  {changes.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Changes:</p>
                      <ul className="space-y-1">
                        {changes.map((change, i) => {
                          const isBreaking = change.toLowerCase().includes('breaking');
                          return (
                            <li 
                              key={i} 
                              className={cn(
                                "flex items-start gap-2 text-sm",
                                isBreaking ? "text-red-600" : "text-gray-600"
                              )}
                            >
                              <span className={cn(
                                "w-1.5 h-1.5 rounded-full mt-1.5 shrink-0",
                                isBreaking ? "bg-red-500" : "bg-gray-400"
                              )} />
                              {change}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
          >
            Close
          </button>
        </div>

        {/* Compare Versions Modal */}
        {showCompareModal && comparisonData && (() => {
          // Determine which version is older/newer (keep original order, just add labels)
          const compResult = compareVersions(comparisonData.versionA.version, comparisonData.versionB.version);
          const isAOlder = compResult < 0;
          const isANewer = compResult > 0;
          
          // Keep the user's selected order for display
          const leftVersion = comparisonData.versionA;
          const rightVersion = comparisonData.versionB;
          const leftLabel = isAOlder ? '(older)' : isANewer ? '(newer)' : '';
          const rightLabel = isAOlder ? '(newer)' : isANewer ? '(older)' : '';
          const leftBg = isAOlder ? 'bg-red-50' : 'bg-green-50';
          const rightBg = isAOlder ? 'bg-green-50' : 'bg-red-50';
          const leftTextColor = isAOlder ? 'text-red-700' : 'text-green-700';
          const rightTextColor = isAOlder ? 'text-green-700' : 'text-red-700';
          const leftLabelColor = isAOlder ? 'text-red-500' : 'text-green-500';
          const rightLabelColor = isAOlder ? 'text-green-500' : 'text-red-500';
          
          return (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl z-10">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[90%] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">
                  Compare Versions
                </h3>
                <button
                  onClick={() => setShowCompareModal(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              {/* Version Headers */}
              <div className="grid grid-cols-2 border-b border-gray-200 shrink-0">
                <div className={cn("px-6 py-3 border-r border-gray-200", leftBg)}>
                  <span className={cn("font-medium", leftTextColor)}>v{leftVersion.version}</span>
                  <span className={cn("text-sm ml-2", leftLabelColor)}>{leftLabel}</span>
                </div>
                <div className={cn("px-6 py-3", rightBg)}>
                  <span className={cn("font-medium", rightTextColor)}>v{rightVersion.version}</span>
                  <span className={cn("text-sm ml-2", rightLabelColor)}>{rightLabel}</span>
                </div>
              </div>
              
              {/* Differences */}
              <div className="flex-1 overflow-y-auto">
                {(() => {
                  const differences = getDifferences(
                    leftVersion.content as Record<string, unknown>,
                    rightVersion.content as Record<string, unknown>
                  );
                  
                  if (differences.length === 0) {
                    return (
                      <div className="flex items-center justify-center py-16 text-gray-500">
                        <div className="text-center">
                          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                          <p className="font-medium">No differences found</p>
                          <p className="text-sm mt-1">The content of these versions is identical</p>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="divide-y divide-gray-200">
                      {differences.map((diff, index) => (
                        <div key={index} className="grid grid-cols-2">
                          {/* Left side */}
                          <div className={cn(
                            "px-6 py-4 border-r border-gray-200",
                            diff.type === 'changed' && (isAOlder ? "bg-red-50/50" : "bg-green-50/50"),
                            diff.type === 'removed' && (isAOlder ? "bg-red-50" : "bg-green-50"),
                            diff.type === 'added' && (isAOlder ? "" : "bg-green-50")
                          )}>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                              {diff.label}
                            </p>
                            {diff.type === 'added' ? (
                              <p className="text-gray-400 italic text-sm">Not present</p>
                            ) : (
                              <p className={cn(
                                "text-sm whitespace-pre-wrap break-words",
                                isAOlder ? "text-red-600" : "text-green-600"
                              )}>
                                {formatValue(diff.key, diff.valueA)}
                              </p>
                            )}
                          </div>
                          
                          {/* Right side */}
                          <div className={cn(
                            "px-6 py-4",
                            diff.type === 'changed' && (isAOlder ? "bg-green-50/50" : "bg-red-50/50"),
                            diff.type === 'added' && (isAOlder ? "bg-green-50" : "bg-red-50"),
                            diff.type === 'removed' && (isAOlder ? "" : "bg-red-50")
                          )}>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                              {diff.label}
                            </p>
                            {diff.type === 'removed' ? (
                              <p className="text-gray-400 italic text-sm">Removed</p>
                            ) : (
                              <p className={cn(
                                "text-sm whitespace-pre-wrap break-words",
                                isAOlder ? "text-green-600" : "text-red-600"
                              )}>
                                {formatValue(diff.key, diff.valueB)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end shrink-0">
                <button
                  onClick={() => setShowCompareModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Create Version Modal */}
        {showCreateModal && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Version</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Version Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newVersion}
                    onChange={(e) => setNewVersion(e.target.value)}
                    placeholder="e.g., 1.0.1"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use semantic versioning (major.minor.patch)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Changelog
                  </label>
                  <textarea
                    value={changelog}
                    onChange={(e) => setChangelog(e.target.value)}
                    placeholder="Describe the changes in this version...&#10;- Change 1&#10;- Change 2"
                    rows={4}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateVersion}
                  disabled={!newVersion.trim() || createVersion.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {createVersion.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Create Version
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

